import React from 'react';
import { StockScreenerData } from './columnDefinitions';

interface ScoreBreakdownProps {
  stock: StockScreenerData;
  activeKPI: string | null;
  hoveredKPI: string | null;
  onKPIClick: (kpiId: string) => void;
  onKPIMouseEnter: (kpiId: string) => void;
  onKPIMouseLeave: () => void;
}

/**
 * Per-indicator breakdown of a custom scoring framework (the user's own score,
 * `score_breakdown` from the backend). The default CC / CSP score has no
 * breakdown here: its parts are on the card, served by the backend (gold
 * phase 2b). The old default-score view, with its browser-side rating bands,
 * earnings classification and setup maths, was unreachable and is deleted.
 */
const ScoreBreakdown: React.FC<ScoreBreakdownProps> = ({ stock }) => {
  if (!stock.score_breakdown) return null;
  return <CustomFrameworkBreakdown stock={stock} breakdown={stock.score_breakdown} />;
};

// =============================================================================
// CUSTOM FRAMEWORK BREAKDOWN — shown when stock has score_breakdown from dynamic scoring
// =============================================================================

const CustomFrameworkBreakdown: React.FC<{
  stock: StockScreenerData;
  breakdown: NonNullable<StockScreenerData['score_breakdown']>;
}> = ({ stock, breakdown }) => {
  const qualityItems = breakdown.indicator_breakdown.filter(i => i.dimension === 'quality');
  const timingItems = breakdown.indicator_breakdown.filter(i => i.dimension === 'timing');

  const pctBarColor = (pct: number) => {
    if (pct >= 0.75) return '#10b981';
    if (pct >= 0.5) return '#f59e0b';
    if (pct >= 0.25) return '#f97316';
    return '#ef4444';
  };

  const renderItems = (items: typeof breakdown.indicator_breakdown) =>
    items.map(item => (
      <div key={item.indicator_key} style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '4px 0', fontSize: '0.78rem',
      }}>
        <span style={{ flex: 1, color: '#cbd5e1' }}>{item.display_name}</span>
        <span style={{ color: '#64748b', minWidth: '50px', textAlign: 'right' }}>
          {item.raw_value != null ? (typeof item.raw_value === 'number' ? item.raw_value.toFixed(1) : item.raw_value) : '—'}
        </span>
        <div style={{ width: '60px', height: '6px', borderRadius: '3px', background: '#1f2937' }}>
          <div style={{
            width: `${item.score_pct * 100}%`, height: '100%',
            borderRadius: '3px', background: pctBarColor(item.score_pct),
            transition: 'width 0.3s',
          }} />
        </div>
        <span style={{ color: '#e2e8f0', minWidth: '35px', textAlign: 'right', fontWeight: 500 }}>
          {item.points_earned.toFixed(0)}/{item.max_points}
        </span>
      </div>
    ));

  return (
    <div style={{ display: 'flex', gap: '16px' }}>
      {/* Quality */}
      <div style={{ flex: 1 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          marginBottom: '6px', paddingBottom: '4px', borderBottom: '1px solid #1f2937',
        }}>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#3b82f6' }}>Quality</span>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            {breakdown.quality_score.toFixed(0)}/{breakdown.quality_max}
          </span>
        </div>
        {renderItems(qualityItems)}
      </div>

      {/* Timing */}
      <div style={{ flex: 1 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          marginBottom: '6px', paddingBottom: '4px', borderBottom: '1px solid #1f2937',
        }}>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#a78bfa' }}>Timing</span>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            {breakdown.timing_score.toFixed(0)}/{breakdown.timing_max}
          </span>
        </div>
        {renderItems(timingItems)}

        {/* Premium modules */}
        {breakdown.premium_breakdown.map(pm => (
          <div key={pm.module_key} style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '4px 0', fontSize: '0.78rem',
          }}>
            <span style={{ flex: 1, color: '#cbd5e1' }}>
              {pm.display_name}
              <span style={{
                marginLeft: '4px', padding: '1px 4px', borderRadius: '3px',
                background: '#4338ca33', color: '#a78bfa', fontSize: '0.6rem',
              }}>
                PRO
              </span>
            </span>
            <span style={{ color: '#64748b', minWidth: '50px', textAlign: 'right' }}>
              {pm.raw_score.toFixed(0)}
            </span>
            <div style={{ width: '60px', height: '6px', borderRadius: '3px', background: '#1f2937' }}>
              <div style={{
                width: `${pm.normalized_score * 100}%`, height: '100%',
                borderRadius: '3px', background: pctBarColor(pm.normalized_score),
                transition: 'width 0.3s',
              }} />
            </div>
            <span style={{ color: '#e2e8f0', minWidth: '35px', textAlign: 'right', fontWeight: 500 }}>
              {pm.points_earned.toFixed(0)}/{pm.max_points}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScoreBreakdown;
